import React, { useState, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ChevronRight, MapPin } from "lucide-react";
import { siteConfig } from "../siteConfig";

const CLOSE_DELAY = 120;
const toSlug = (area) => area.toLowerCase().replace(/\s+/g, "-");

const NavServiceAreasDropdown = ({ scrolled }) => {
  const [open, setOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const closeTimer = useRef(null);

  const scheduleClose = useCallback(() => {
    closeTimer.current = setTimeout(() => setOpen(false), CLOSE_DELAY);
  }, []);

  const cancelClose = useCallback(() => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
  }, []);

  const labelColor = scrolled ? "text-gray-700" : "text-white";

  return (
    <>
      {/* ── DESKTOP DROPDOWN ── */}
      <div
        className="hidden lg:block relative"
        onMouseEnter={() => { cancelClose(); setOpen(true); }}
        onMouseLeave={scheduleClose}
      >
        {/* Trigger */}
        <button
          className={`flex items-center gap-1 text-[15px] font-semibold transition-colors hover:opacity-70 ${labelColor}`}
        >
          Service Areas
          <ChevronDown
            className={`w-3.5 h-3.5 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
          />
        </button>

        {/* Panel */}
        <div
          className={`absolute top-full left-1/2 -translate-x-1/2 mt-3 z-50
            w-[min(280px,92vw)] rounded-2xl overflow-hidden shadow-2xl
            border border-white/10 backdrop-blur-sm
            transition-all duration-200 ease-out
            ${open
              ? "opacity-100 translate-y-0 pointer-events-auto"
              : "opacity-0 -translate-y-2 pointer-events-none"
            }`}
          style={{ background: "rgba(14,18,12,0.96)" }}
          onMouseEnter={cancelClose}
          onMouseLeave={scheduleClose}
        >
          {/* Panel header */}
          <div className="px-5 pt-4 pb-3 border-b border-white/10">
            <p className="text-xs font-black uppercase tracking-widest" style={{ color: siteConfig.colors.primary }}>
              Areas We Serve
            </p>
          </div>

          {/* Area list */}
          <ul className="p-2">
            {siteConfig.serviceAreas.map((area) => (
              <li key={area}>
                <Link
                  to={`/service-areas/${toSlug(area)}`}
                  onClick={() => setOpen(false)}
                  className="group flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors hover:bg-white/5"
                >
                  <span
                    className="shrink-0 w-7 h-7 rounded-md flex items-center justify-center"
                    style={{ backgroundColor: `${siteConfig.colors.primary}18` }}
                  >
                    <MapPin className="w-3.5 h-3.5" style={{ color: siteConfig.colors.primary }} />
                  </span>

                  <span className="flex-1 text-sm font-bold text-white group-hover:text-primary transition-colors duration-150 uppercase tracking-wide">
                    {area}
                  </span>

                  <ChevronRight className="shrink-0 w-3.5 h-3.5 text-white/25 transition-transform duration-150 group-hover:translate-x-1" />
                </Link>
              </li>
            ))}
          </ul>

          {/* Footer */}
          <div className="px-5 py-3 border-t border-white/10">
            <Link
              to="/service-areas"
              onClick={() => setOpen(false)}
              className="flex items-center justify-between text-xs font-bold uppercase tracking-widest transition-opacity hover:opacity-70"
              style={{ color: siteConfig.colors.primary }}
            >
              View All Areas
              <ChevronRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </div>

      {/* ── MOBILE ACCORDION ── */}
      <div className="lg:hidden border-b border-gray-100">
        <button
          className="w-full flex items-center justify-between py-3 text-gray-800 font-semibold text-sm"
          onClick={() => setMobileOpen((v) => !v)}
        >
          Service Areas
          <ChevronDown
            className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${mobileOpen ? "rotate-180" : ""}`}
          />
        </button>

        <div
          className="overflow-hidden transition-all duration-300 ease-out"
          style={{ maxHeight: mobileOpen ? "900px" : "0px" }}
        >
          <ul className="pb-2 pl-3 flex flex-col gap-0.5">
            {siteConfig.serviceAreas.map((area) => (
              <li key={area}>
                <Link
                  to={`/service-areas/${toSlug(area)}`}
                  className="flex items-center gap-3 py-2.5 text-sm text-gray-700 font-medium uppercase tracking-wide"
                >
                  <span
                    className="w-6 h-6 rounded-md flex items-center justify-center shrink-0"
                    style={{ backgroundColor: `${siteConfig.colors.primary}18` }}
                  >
                    <MapPin className="w-3 h-3" style={{ color: siteConfig.colors.primary }} />
                  </span>
                  {area}
                </Link>
              </li>
            ))}
            <li>
              <Link
                to="/service-areas"
                className="flex items-center gap-1 py-2 text-xs font-bold uppercase tracking-widest"
                style={{ color: siteConfig.colors.primary }}
              >
                All Areas <ChevronRight className="w-3 h-3" />
              </Link>
            </li>
          </ul>
        </div>
      </div>
    </>
  );
};

export default NavServiceAreasDropdown;
